/** Jobs (S50), Add a job (S51), a job (S52) and the photo library. docs/50 §4.5, docs/52 §3.6, docs/53 §7.8. */
import { Banner, Button, Card, Chip, EmptyState, Icon, List, Row, SelectField, TextArea, type Tone } from '@ff/app-ui';
import { jobs as J } from '@ff/contracts';
const { UPLOAD_CONTENT_TYPES, UPLOAD_LIMITS } = J;
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { z } from 'zod';
import { ApiError, errorText, idemKey } from '../lib/api.ts';
import { invalidate, online, send, setCached, useOnline, useQuery, useSend } from '../lib/data.ts';
import { nearestTown } from '../lib/exif.ts';
import { dayLabel, rowTime, shortDate } from '../lib/format.ts';
import { enqueue } from '../lib/outbox.ts';
import { location, navigate, scoped } from '../lib/router.ts';
import { can, useReady } from '../lib/session.ts';
import { useStore } from '../lib/store.ts';
import { haptic, toast, tz } from '../lib/ui.ts';
import { processPhoto, uploadBlob, type Processed } from '../lib/upload.ts';
import { S } from '@ff/app-ui/copy';
import { Loaded, LoadError, PrimaryAction, Screen, Section } from '../ui/common.tsx';
import { Split } from '../ui/split.tsx';

type JobStatus = z.infer<typeof J.JobSummary>['status'];
const JOB_TONE: Record<JobStatus, Tone> = { received: 'neutral', drafting: 'warn', in_review: 'warn', awaiting_client: 'new', published: 'ok', rejected: 'muted' };

export function JobsScreen({ id }: { id?: string }): JSX.Element {
  return <Split list={<JobList selected={id} />} detail={id ? <JobDetail id={id} /> : null} />;
}

function JobList({ selected }: { selected?: string }): JSX.Element {
  const s = useReady();
  const loc = useStore(location);
  const zone = useStore(tz);
  const q = useQuery('jobs', { query: loc.scope ? { location: loc.scope } : {} });
  return (
    <Screen title={S.jobs.title} query={q}>
      <Loaded q={q}>
        {(d) =>
          d.items.length ? (
            <div class="stack">
              <List label={S.jobs.title}>
                {d.items.map((j) => (
                  <li key={j.id}>
                    <Row
                      href={scoped(`/jobs/${j.id}`, loc.scope)}
                      current={selected === j.id}
                      lead={j.coverThumbUrl ? <img class="row-thumb" src={j.coverThumbUrl} alt="" loading="lazy" decoding="async" width={56} height={56} /> : <span class="row-thumb row-thumb-empty"><Icon name="image" /></span>}
                      title={j.note ?? [j.serviceName, j.townName].filter(Boolean).join(', ')}
                      meta={[[j.serviceName, j.townName].filter(Boolean).join(' · '), S.jobs.photoCount(j.photoCount)].filter(Boolean).join(' · ')}
                      time={rowTime(j.createdAt, zone)}
                      chip={<Chip tone={JOB_TONE[j.status]}>{S.jobs.status[j.status]}</Chip>}
                    />
                  </li>
                ))}
              </List>
              {can(s.membership, 'media.read') && (
                <a class="section-link" href={scoped('/jobs/photos', loc.scope)}>
                  <Icon name="image" size={20} /> {S.jobs.library}
                </a>
              )}
            </div>
          ) : (
            <EmptyState title={S.jobs.firstRunTitle} body={S.jobs.firstRun} />
          )
        }
      </Loaded>
      {can(s.membership, 'jobs.create') && (
        <PrimaryAction href={scoped('/jobs/new', loc.scope)} icon="camera">
          {S.jobs.add}
        </PrimaryAction>
      )}
    </Screen>
  );
}

function JobDetail({ id }: { id: string }): JSX.Element {
  const loc = useStore(location);
  const zone = useStore(tz);
  const s = useReady();
  const q = useQuery('job', { params: { id } });
  if (q.error && !q.data) return <div class="screen"><LoadError error={q.error} onRetry={q.refetch} /></div>;
  const title = q.data ? ([q.data.serviceName, q.data.townName].filter(Boolean).join(' in ') || S.jobs.title) : S.jobs.title;
  const toggle = async (assetId: string, excluded: boolean) => {
    try {
      const m = await send('jobMedia', { params: { id, asset: assetId }, body: { excluded } });
      setCached('job', { params: { id } }, (prev) => (prev ? { ...prev, media: prev.media.map((x) => (x.id === m.id ? m : x)) } : prev));
    } catch (e) {
      toast(errorText(e, S.errors.offlineSend));
    }
  };
  return (
    <Screen title={title} back={scoped('/jobs', loc.scope)} query={q}>
      <Loaded q={q} skeleton="detail">
        {(j) => (
          <div class="stack" data-gap="l">
            <div class="cluster">
              <Chip tone={JOB_TONE[j.status]}>{S.jobs.status[j.status]}</Chip>
              <span class="t-small t-muted">{shortDate(j.createdAt, zone)}</span>
            </div>
            {j.media.length > 0 && (
              <Section title={S.jobs.photos} id="job-photos">
                <ul role="list" class="photo-list">
                  {j.media.map((m, i) => (
                    <li key={m.id} class="photo-item" data-excluded={m.excluded ? '' : undefined}>
                      {m.processing ? <span class="library-pending" role="img" aria-label={`${S.jobs.photoN(i + 1, j.media.length)}. ${S.photos.state.checking}`}><Icon name="image" size={28} /></span> : <img src={m.thumbUrl} alt={S.jobs.photoN(i + 1, j.media.length)} loading="lazy" decoding="async" />}
                      {m.processing && <p class="t-small t-muted">{S.photos.checkingNote}</p>}
                      {(m.screenState === 'flagged' || m.screenState === 'rejected') && m.screenNote && <p class="thumb-flag">{m.screenNote}</p>}
                      {can(s.membership, 'jobs.update') && (
                        <Button size="sm" variant={m.excluded ? 'secondary' : 'quiet'} onClick={() => void toggle(m.id, !m.excluded)} aria-label={`${m.excluded ? S.jobs.useAgain : S.jobs.dontUse}, ${S.jobs.photoN(i + 1, j.media.length)}`}>
                          {m.excluded ? S.jobs.useAgain : S.jobs.dontUse}
                        </Button>
                      )}
                      {m.excluded && <span class="t-small t-muted">{S.jobs.excluded}</span>}
                    </li>
                  ))}
                </ul>
              </Section>
            )}
            {(j.note || j.transcript) && (
              <Section title={S.jobs.yourWords} id="job-words">
                <Card>
                  <p>{j.note ?? j.transcript}</p>
                </Card>
              </Section>
            )}
            <Section title={S.jobs.whereWent} id="job-outputs">
              {j.outputs.length ? (
                <List>
                  {j.outputs.map((o) => (
                    <li key={o.id}>
                      <Row
                        href={o.liveUrl ?? undefined}
                        external={!!o.liveUrl}
                        lead={<Icon name={o.kind.startsWith('gbp') ? 'globe' : 'file'} size={22} />}
                        title={S.jobs.output[o.kind]}
                        meta={o.publishedAt ? `${S.jobs.outputState[o.state]} · ${shortDate(o.publishedAt, zone)}` : S.jobs.outputState[o.state]}
                        chip={o.state === 'live' ? <Chip tone="ok">{S.jobs.outputState.live}</Chip> : null}
                      />
                    </li>
                  ))}
                </List>
              ) : (
                <p class="t-muted">{S.jobs.noOutputs}</p>
              )}
            </Section>
          </div>
        )}
      </Loaded>
    </Screen>
  );
}

// ------------------------------------------------------------------------------------------ Add a job

interface Pick {
  key: string;
  name: string;
  url: string;
  state: 'processing' | 'uploading' | 'done' | 'failed' | 'waiting';
  progress: number;
  processed: Processed | null;
  uploadId: string | null;
  error: string | null;
}

const PHOTO_TYPES = UPLOAD_CONTENT_TYPES.photo as readonly string[];

export function AddJobScreen(): JSX.Element {
  const loc = useStore(location);
  const isOnline = useOnline();
  const opts = useQuery('jobOptions', { query: loc.scope ? { location: loc.scope } : {} });
  const [picks, setPicks] = useState<Pick[]>([]);
  const [note, setNote] = useState('');
  const [townId, setTownId] = useState<string>('');
  const [serviceId, setServiceId] = useState<string>('');
  const [townGuess, setTownGuess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ readyBy: string | null; queued: boolean } | null>(null);
  const [key] = useState(idemKey);
  const act = useSend();
  const picksRef = useRef<Pick[]>([]);
  picksRef.current = picks;
  const camRef = useRef<HTMLInputElement>(null);
  const libRef = useRef<HTMLInputElement>(null);
  const live = useRef<HTMLParagraphElement>(null);

  useEffect(() => () => picksRef.current.forEach((p) => URL.revokeObjectURL(p.url)), []);

  const update = (k: string, patch: Partial<Pick>) => setPicks((ps) => ps.map((p) => (p.key === k ? { ...p, ...patch } : p)));

  const upload = async (p: Pick) => {
    if (!p.processed) return;
    if (!online.get()) {
      update(p.key, { state: 'waiting' });
      return;
    }
    update(p.key, { state: 'uploading', progress: 0.05, error: null });
    try {
      const id = await uploadBlob(p.processed.blob, p.processed.contentType, 'photo', 'job', (f) => update(p.key, { progress: f }));
      update(p.key, { state: 'done', progress: 1, uploadId: id });
    } catch (e) {
      update(p.key, { state: e instanceof ApiError && e.offline ? 'waiting' : 'failed', error: S.jobs.uploadFailed });
    }
  };

  const add = async (files: FileList | null) => {
    if (!files?.length) return;
    setError(null);
    const room = UPLOAD_LIMITS.filesPerJob - picksRef.current.length;
    const list = Array.from(files).slice(0, Math.max(0, room));
    if (files.length > room) setError(S.jobs.tooMany);
    for (const f of list) {
      if (!f.type.startsWith('image/') && !PHOTO_TYPES.includes(f.type)) {
        setError(S.jobs.notPhoto(f.name));
        continue;
      }
      if (f.size > UPLOAD_LIMITS.photoBytes) {
        setError(S.jobs.tooBig(f.name));
        continue;
      }
      const p: Pick = { key: idemKey(), name: f.name, url: URL.createObjectURL(f), state: 'processing', progress: 0, processed: null, uploadId: null, error: null };
      setPicks((ps) => [...ps, p]);
      // Uploads start the moment photos are picked (docs/52 S51).
      void processPhoto(f).then((processed) => {
        const next = { ...p, processed, state: 'uploading' as const };
        update(p.key, { processed });
        if (processed.gps && !townId && opts.data) {
          const t = nearestTown(processed.gps, opts.data.towns);
          if (t) {
            setTownId(String(t.id));
            setTownGuess(t.name);
          }
        }
        void upload(next);
      });
    }
  };

  const move = (i: number, d: -1 | 1) =>
    setPicks((ps) => {
      const a = ps.slice();
      const j = i + d;
      if (j < 0 || j >= a.length) return ps;
      [a[i], a[j]] = [a[j]!, a[i]!];
      return a;
    });

  const uploaded = picks.filter((p) => p.state === 'done').length;

  const submit = async (e: Event) => {
    e.preventDefault();
    if (!picks.length && !note.trim()) {
      setError(S.jobs.needSomething);
      return;
    }
    setError(null);
    const body = {
      ...(note.trim() ? { note: note.trim() } : {}),
      ...(townId ? { townId: Number(townId) } : {}),
      ...(serviceId ? { serviceId } : {}),
      ...(loc.scope ? { locationId: loc.scope } : {}),
      idempotencyKey: key,
    };
    const pending = picks.filter((p) => p.state !== 'done');
    if (!online.get() || pending.length) {
      // Anything not up yet waits in the outbox with its photo, and the job sends when all are up (docs/54 M4).
      const blobs: Record<string, Blob> = {};
      for (const p of pending) if (p.processed) blobs[p.key] = p.processed.blob;
      await enqueue(
        { id: key, kind: 'job', body: { ...body, uploadIds: picks.filter((p) => p.state === 'done').map((p) => p.uploadId!) }, photos: pending.filter((p) => p.processed).map((p) => ({ key: p.key, contentType: p.processed!.contentType, uploadId: null })) },
        blobs,
      );
      if (online.get()) void import('../lib/outbox.ts').then((m) => m.flush());
      setDone({ readyBy: null, queued: true });
      return;
    }
    const out = await act.run(() => send('jobCreate', { body: { ...body, uploadIds: picks.map((p) => p.uploadId!) } }));
    if (!out) return;
    haptic();
    invalidate('jobs', 'home');
    setDone({ readyBy: out.readyBy, queued: false });
  };

  if (done)
    return (
      <Screen title={done.queued ? S.jobs.queuedTitle : S.jobs.gotIt} back={scoped('/jobs', loc.scope)}>
        <div class="stack done-panel">
          <Icon name={done.queued ? 'upload' : 'check'} size={40} />
          <p class="t-heading">{done.queued ? S.jobs.queuedLead : S.jobs.gotItLead(done.readyBy ? dayLabel(done.readyBy) : '')}</p>
          <Button variant="primary" href={scoped('/jobs', loc.scope)}>{S.jobs.title}</Button>
          <Button variant="quiet" onClick={() => navigate(scoped('/jobs/new', loc.scope), { replace: true })}>{S.jobs.add}</Button>
        </div>
      </Screen>
    );

  return (
    <Screen title={S.jobs.add} back={scoped('/jobs', loc.scope)} lead={S.jobs.addLead}>
      <form class="stack form-narrow" data-gap="l" onSubmit={submit} noValidate>
        <section class="stack" data-gap="m" aria-labelledby="photos-h">
          <h2 id="photos-h" class="t-heading">{S.jobs.photos}</h2>
          <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => (void add((e.currentTarget as HTMLInputElement).files), ((e.currentTarget as HTMLInputElement).value = ''))} />
          <input ref={libRef} type="file" accept="image/*,.heic,.heif" multiple hidden onChange={(e) => (void add((e.currentTarget as HTMLInputElement).files), ((e.currentTarget as HTMLInputElement).value = ''))} data-testid="photo-input" />
          <div class="btn-row" data-equal>
            <Button variant="primary" icon="camera" onClick={() => camRef.current?.click()}>
              {S.jobs.takePhoto}
            </Button>
            <Button icon="image" onClick={() => libRef.current?.click()}>
              {S.jobs.choosePhotos}
            </Button>
          </div>
          <p class="t-small t-muted">
            <Icon name="shield" size={16} /> {S.jobs.privacy}
          </p>
          {picks.length > 0 && (
            <>
              <p class="t-small" ref={live} aria-live="polite">{S.jobs.uploading(uploaded, picks.length)}</p>
              <ul role="list" class="thumbs">
                {picks.map((p, i) => (
                  <li key={p.key} class="thumb-wrap">
                    <div class="thumb" data-state={p.state}>
                      <img src={p.url} alt={S.jobs.photoN(i + 1, picks.length)} />
                      <span class="thumb-order" aria-hidden="true">{i + 1}</span>
                      <span class="thumb-state" aria-hidden="true">
                        {p.state === 'done' ? <Icon name="check" /> : p.state === 'failed' ? <Icon name="refresh" /> : p.state === 'waiting' ? <Icon name="offline" /> : <UploadRing f={p.progress} />}
                      </span>
                    </div>
                    <div class="thumb-actions">
                      {p.state === 'failed' && (
                        <button type="button" class="icon-btn" onClick={() => void upload(p)}>
                          <Icon name="refresh" size={18} /> <span>{S.jobs.retry}</span>
                        </button>
                      )}
                      <button type="button" class="icon-btn icon-only" aria-label={S.jobs.moveUp(i + 1)} disabled={i === 0} onClick={() => move(i, -1)}>
                        <Icon name="chevron-left" size={20} />
                      </button>
                      <button type="button" class="icon-btn icon-only" aria-label={S.jobs.moveDown(i + 1)} disabled={i === picks.length - 1} onClick={() => move(i, 1)}>
                        <Icon name="chevron-right" size={20} />
                      </button>
                      <button
                        type="button"
                        class="icon-btn icon-only"
                        aria-label={S.jobs.removePhoto(i + 1)}
                        onClick={() => {
                          URL.revokeObjectURL(p.url);
                          setPicks((ps) => ps.filter((x) => x.key !== p.key));
                        }}
                      >
                        <Icon name="trash" size={20} />
                      </button>
                    </div>
                    {p.error && <p class="field-error">{p.error}</p>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        <TextArea label={S.jobs.whatDid} hint={S.jobs.whatDidHint} value={note} onInput={setNote} maxLength={2000} rows={3} />

        {opts.data && opts.data.towns.length > 0 && (
          <div class="stack" data-gap="s">
            <SelectField label={S.jobs.town} value={townId} onChange={(v) => (setTownId(v), setTownGuess(null))} placeholder={S.jobs.pick} options={opts.data.towns.map((t) => ({ value: String(t.id), label: t.name }))} />
            {townGuess && <p class="t-small t-muted">{S.jobs.townGuess(townGuess)}</p>}
          </div>
        )}
        {opts.data && opts.data.services.length > 0 && (
          <SelectField label={S.jobs.service} value={serviceId} onChange={setServiceId} placeholder={S.jobs.pick} options={opts.data.services.map((s) => ({ value: s.id, label: s.name }))} />
        )}

        {!isOnline && <Banner tone="warn" icon="offline" title={S.jobs.queuedLead} />}
        {(error || act.error) && <Banner tone="danger" role="alert" title={error ?? errorText(act.error, S.errors.offlineSend)} />}
        <div class="sticky-submit">
          <Button type="submit" variant="primary" size="lg" block busy={act.busy} icon="send">
            {S.jobs.send}
          </Button>
        </div>
      </form>
    </Screen>
  );
}

function UploadRing({ f }: { f: number }): JSX.Element {
  const r = 10;
  const c = 2 * Math.PI * r;
  return (
    <svg class="up-ring" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r={r} fill="none" stroke="currentColor" stroke-opacity="0.3" stroke-width="3" />
      <circle cx="12" cy="12" r={r} fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-dasharray={c} stroke-dashoffset={c * (1 - Math.max(0.05, f))} />
    </svg>
  );
}
